import { cmsDataService, ExtensionDef, navigationService, OccContextPropsApi, locationService } from '..'
import { Pages, PagesEnum, PagesNames, PagesNamesToExclude } from '../pages'
import { api } from './api'
import { InitialDataResponse, OccApiProps, PageBase, PageResponse } from './models'
import { RichTextInterface } from '../cmsTypes/richTextInterface'
import { RecaptchaV2Config } from '@vista-digital/occ-ui/src/lib/subcomponents/captcha/recaptcha-v2/types/config'

export interface LoadPageModel {
    data: PageResponse
    page: PagesEnum
    id?: string
    usePageUrl?: boolean
}

export interface ConcessionCinemaCheck {
    siteId: string
}

export interface InitializeResult {
    occConfiguration: OccContextPropsApi
    occApi: OccApiProps
    gtmId: string
    rumId: string
    podiumChatBotOrgId: string
    recaptcha: RecaptchaV2Config
}

class CmsService {
    private data: InitialDataResponse

    loadPageComponents = async <T extends PageResponse>(
        /*{ data, page }*/ model: LoadPageModel,
        { signal }
    ): Promise<T> => {
        let componentsNames = []
        if (model.data) {
            for (let key in model.data.components) {
                componentsNames.push(key)
            }
        }

        let friendlyUrl = navigationService.getLocation().pathname ?? window.location.pathname
        let extraQP = ''
        if (model.page && model.page && Pages[model.page].parent && Pages[model.page].customAlias) {
            friendlyUrl = Pages[Pages[model.page].parent].urls[0]
            extraQP = '&descendantOfType=' + Pages[model.page].customAlias
        }

        // Used for the 404 page which can be on any url
        if (model.usePageUrl) friendlyUrl = Pages[model.page].urls[0]

        // Make a request, populate the fields

        let pageData: T
        try {
            const [data, statusCode] = await api.GET<T>(
                api.Page +
                    '?friendly=' +
                    friendlyUrl +
                    this.buildQueryArray('components', componentsNames) +
                    this.buildQueryArray('ancestorComponents', [
                        'location.footerLinks',
                        'location.LocationAnnouncement',
                    ]) +
                    extraQP
            )
            pageData = data
        } catch (error) {
            navigationService.goToPage(PagesEnum.ErrorNotFound)
        }

        if (pageData?.ancestorComponents?.location?.footerLinks ?? false) {
            locationService.setlocationName(pageData.name)
            locationService.setlocationLinks(pageData.ancestorComponents.location.footerLinks)
        } else {
            locationService.setlocationName(null)
            locationService.setlocationLinks(null)
        }

        if (pageData?.ancestorComponents?.location?.LocationAnnouncement ?? false) {
            locationService.setLocationAnnouncement(pageData.ancestorComponents.location.LocationAnnouncement)
        } else {
            locationService.setLocationAnnouncement(null)
        }

        if (model.data) {
            if (!pageData.components) pageData.components = model.data.components
            else {
                componentsNames.forEach(name => {
                    if (!pageData.components[name]) pageData.components[name] = model.data.components[name]
                })
            }
        }

        setTimeout(() => {
            // To properly render all occs
            window.dispatchEvent(new Event('resize'))
        }, 100)

        return pageData
    }

    initialize = async (): Promise<InitializeResult> => {
        let pages = PagesNames(true)

        const [data, statusCode] = await api.GET<InitialDataResponse>(
            api.InitialData + '?excludedPages=' + PagesNamesToExclude().join(',')
        )
        this.data = data

        // TODO: temporary until occ context is ready
        this.data.occConfiguration = { ...new OccContextPropsApi(), ...this.data.occConfiguration }
        cmsDataService.set(this.data)

        // Set up pages
        pages.forEach(page => {
            if (Pages[page].parent) {
                let parentUrls = this.data.pages[Pages[page].parent]
                Pages[page].urls = parentUrls.map(u => u + Pages[page].params.map(p => ':' + p).join('/'))
            } else Pages[page].urls = this.data.pages[page]

            if (Pages[page].subPages && Pages[page].subPages.length) {
                Pages[page].subPages.forEach(subPage => (subPage.urls = this.data.pages[subPage.alias]))
            }
        })

        return {
            occConfiguration: this.data.occConfiguration,
            occApi: this.data.api,
            gtmId: this.data.gtmId,
            rumId: this.data.rumId,
            podiumChatBotOrgId: this.data.podiumChatBotOrgId,
            recaptcha: this.data.captcha,
        }
    }

    loadPageList = async <T extends PageBase>(properties: string[], pageUrl: string = null): Promise<T[]> => {
        const [data, statusCode] = await api.GET<T[]>(
            api.PageList +
                '?friendly=' +
                (pageUrl ?? navigationService.getLocation().pathname ?? window.location.pathname) +
                this.buildQueryArray('properties', properties)
        )

        return data
    }

    get() {
        return this.data
    }

    listSubPages = async <T extends PageBase>(page: PagesEnum, properties: string[]): Promise<T[]> => {
        const [data, statusCode] = await api.GET<T[]>(
            api.PageList + '?friendly=' + Pages[page].urls[0] + this.buildQueryArray('properties', properties)
        )

        return data
    }

    loadConfigurationComponents = async <T>(model: { data: any }, { signal }): Promise<T> => {
        let componentsNames = []
        if (model.data) {
            for (let key in model.data) {
                componentsNames.push(key)
            }
        }

        let friendlyUrl = navigationService.getLocation().pathname ?? window.location.pathname
        const [pageData, statusCode] = await api.GET<T>(
            api.Configuration + '?friendly=' + friendlyUrl + this.buildQueryArray('components', componentsNames)
        )

        if (pageData) {
            for (let component of componentsNames) {
                if (pageData[component]) model.data[component] = pageData[component]
            }
        }

        return model.data
    }

    getExtensions = async <T>(extension: ExtensionDef, urlParameter = ''): Promise<T[]> => {
        const dataPromise = api.GET(api.Extensions + '/' + extension.name + '/' + urlParameter)
        const attributesPromise = extension.mapAttributes
            ? api.GET(api.Attributes(extension.mapAttributes))
            : Promise.resolve(null)

        const promise = await Promise.all([dataPromise, attributesPromise])

        return extension.mapper(promise[0], promise[1])
    }

    filmExtension = async (model: LoadPageModel): Promise<RichTextInterface> => {
        const [data, statusCode] = await api.GET<RichTextInterface>(`${api.FilmExtension}/${model.id}`)
        return data
    }

    checkConcessionsEnabledForSite = async (model: ConcessionCinemaCheck): Promise<boolean> => {
        const [data, statusCode] = await api.GET<boolean>(`${api.ConcessionEnabled}/${model.siteId}`)
        return data
    }

    getSoldOutItems = async (siteId: string, showDate: string): Promise<string[]> => {
        const [data, statusCode] = await api.GET<string[]>(api.SoldOutItems(siteId, showDate))
        return data
    }

    getSoldOutItemsQuantity = async (siteId: string, showDate: string): Promise<{ [key: string]: number }> => {
        const [data, statusCode] = await api.GET<{ [key: string]: number }>(api.SoldOutItemsQuantity(siteId, showDate))
        return data
    }

    private buildQueryArray = (queryName: string, list: string[]) => {
        if (list && list.length) {
            return list.map(e => `&${queryName}=${e}`).join('')
        }
        return ''
    }
}

export const cmsService = new CmsService()
